import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum, TripTypeEnum, SHUTTLE_PRODUCT_CODE } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse, StationsListType } from "../../../Orchestrator/Types/Index";
import { IGetBookingDetailsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { showNotification } from "../../../Services/notificationService";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getListOfServiceRequestAction } from "../ServiceRequestDetails/GetListOfServiceRequestAction";
import { getUpgradePremiumOfferAction } from "../UpgradePremium/GetUpgradePremiumOfferAction";
import { requestUpgradeOffersForBooking } from "../UpgradePremium/UpgradePremiumHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { PassengerPrmNeedTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getStops } from "../../../app/api/getStops";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { transformStops } from "../Search/GetStopsAction";

dayjs.extend(utc);
dayjs.extend(timezone);

const shouldRequestUpgrade = (booking: any): boolean => {
    if (!booking) return false;
    const trips: any[] = booking?.trips || [];
    const bookedOffers: any[] = booking?.bookedOffers || [];
    const passengers: any[] = booking?.passengers || [];

    if (trips.length === 0) return false;
    const earliestStart = trips
        .map((t: any) => t?.startTime)
        .filter(Boolean)
        .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!earliestStart) return false;
    const tripDateDubai = dayjs(earliestStart).tz("Asia/Dubai");
    const nowDubai = dayjs().tz("Asia/Dubai");
    const isUpcoming = tripDateDubai.isAfter(nowDubai);
    if (!isUpcoming) return false;

    const hasRefundedOrReleased = (bookedOffers || []).some((offer: any) =>
        (offer?.admissions || [])?.every((adm: any) => adm?.status === "REFUNDED" || adm?.status === "RELEASED")
    );
    if (hasRefundedOrReleased) return false;

    const noWheelchairPax = (passengers || []).every((p: any) => !(p?.prmNeeds || []).includes(PassengerPrmNeedTypeEnum.Wheelchair));
    if (!noWheelchairPax) return false;

    return true;
}

const hasOnlyShuttleLegs = (booking: any): boolean => {
    if (!booking) return false;
    const bookedOffers: any[] = booking?.bookedOffers || [];
    
    if (bookedOffers?.length === 0) return false;
    
    const allOffersAreShuttle = bookedOffers?.every((offer: any) => {
        const products = offer?.products || [];
        if (products?.length === 0) return false;
        return products?.every((product: any) => product?.code === SHUTTLE_PRODUCT_CODE);
    });
    
    return allOffersAreShuttle;
}

const areAllPassengersInLatestTripsAlreadyUpgraded = (booking: any): boolean => {
    if (!booking) return false;
    const bookedOffers: any[] = booking?.bookedOffers || [];
    const trips: any[] = booking?.trips || [];
    
    if (bookedOffers?.length === 0 || trips?.length === 0) return false;
    
    const latestOutboundTrip = [...trips]?.reverse()?.find(trip => trip?.direction === TripTypeEnum.OUT_BOUND);
    const latestInboundTrip = [...trips]?.reverse()?.find(trip => trip?.direction === TripTypeEnum.IN_BOUND);
    
    const latestTrips = [latestOutboundTrip, latestInboundTrip].filter(Boolean);
    
    if (latestTrips.length === 0) return false;
    
    const latestTripIds = latestTrips?.map(trip => trip.id);
    const latestOffers = bookedOffers?.filter(offer => 
        latestTripIds.includes(offer.tripCoverage?.coveredTripId)
    );
    
    const activeLatestOffers = latestOffers?.filter(offer => {
        const admissions = offer?.admissions || [];
        return !admissions.every((adm: any) => adm?.status === "EXCHANGED");
    });
    
    if (activeLatestOffers?.length === 0) return false;
    
    const trainOffers = activeLatestOffers?.filter((offer: any) => {
        const products = offer?.products || [];
        const productCode = products[0]?.code;
        return productCode !== SHUTTLE_PRODUCT_CODE;
    });
    
    if (trainOffers?.length === 0) {    
        const hasExchangeFees = activeLatestOffers?.some((offer: any) => {
            const fees = offer?.fees || [];
            return fees.some((fee: any) => 
                fee?.type === "MODIFICATION_FEE" || 
                fee?.type === "EXCHANGE_FEE" ||
                fee?.summary?.toLowerCase().includes("exchange")
            );
        });
        
        const allOffersForLatestTrips = bookedOffers?.filter(offer => 
            latestTripIds.includes(offer.tripCoverage?.coveredTripId)
        );
        const hasExchangedAdmissions = allOffersForLatestTrips?.some((offer: any) => {
            const admissions = offer?.admissions || [];
            return admissions.some((adm: any) => adm?.status === "EXCHANGED");
        });
        
        return hasExchangeFees || hasExchangedAdmissions;
    }
    
    const allTrainOffersArePremium = trainOffers?.every((offer: any) => {
        const products = offer?.products || [];
        return products.some((product: any) => product?.code === "PFF");
    });
    
    return allTrainOffersArePremium;
}
import { getBookedCommentsAction } from "./getBookedCommentsAction";
import { getBookingJourneyDetailAction } from "./GetBookingJourneyDetailAction";
import { getBookingPaymentDataAction } from "./GetBookingPaymentDataAction";
import { isBookingMultiBooking, preferTrainTrips } from "../../../Lib/Utils/booking";

class GetBookingDetailDataAction extends BaseAction<void, IGetBookingDetailsActionParams> {

    override async run(): Promise<void> {
        try {
            const { bookingId, direction, displayPassengerData, selectedTripsData, isManageSeating = false, isAnonymous = false } = this.params!;

            // Viewing booking-details means the user has navigated AWAY
            // from any active manage-seating / addons session — even if
            // they didn't pay. Clear both the pending-refund queue and
            // the refund-quote cache so the booking summary doesn't
            // render a stale [Refund] badge on a FULFILLED ancillary the
            // user only tentatively deselected. If they re-enter
            // manage-seating they'll re-queue and re-probe from scratch.
            updateRecoilState("pendingRefundFulfillmentIdsAtom", null);
            updateRecoilState("refundOffersByFulfillmentIdAtom", null);

            if (bookingId) {
                const response: any = await getBooking(bookingId);

                const stopResponse: any = await getStops();
                const stops = stopResponse.status === "ok" ? stopResponse.answer.stops : [];
                if (stops.length > 0) {
                    const config = await getRuntimeConfig();
                    let StationLists = transformStops(stops, config.NEXT_PUBLIC_STOP_EXTERNAL_CODE)
                    updateRecoilState<StationsListType>("stopsResponseAtom", StationLists);
                }
                
                //Get Booking Payment Data API.
                getBookingPaymentDataAction.setParams({ bookingId: bookingId });
                getBookingPaymentDataAction.execute();

                getBookedCommentsAction.setParams({ bookingId })
                await getBookedCommentsAction.execute();
                if (getBookedCommentsAction.status.executionStatus === ExecutionStatus.SUCCESS && getBookedCommentsAction.result) {
                    getBookingJourneyDetailAction.setParams({ bookingComments: getBookedCommentsAction.result.items })
                    await getBookingJourneyDetailAction.execute();
                }

                if (response.status === 'ok' && response.answer) {
                    let bookingResponse: IBookingResponse = response.answer;
                    const { booking } = bookingResponse;

                    /**
                     * ---------------------------------------------------------------
                     * This below logic is to check that booking is modified or not.
                     * This is required to check for book it again.
                     * If booking is modified then no need to show book it again
                     * Booking modified means, Cancel, Exchange, Date change etc.
                    */
                    let noModificationOnBooking: boolean = true
                    for (let i = 0; i < booking.bookedOffers.length; i++) {
                        const bookAdmissions = booking.bookedOffers[i].admissions;
                        const isAnyNotFulfilled = bookAdmissions.find(d => d.status !== "FULFILLED")
                        if (isAnyNotFulfilled) {
                            noModificationOnBooking = false
                            break;
                        }
                    }
                    bookingResponse = {
                        ...bookingResponse,
                        booking: {
                            ...bookingResponse.booking,
                            noModificationOnBooking: noModificationOnBooking
                        }
                    }
                    /*---------------------------------------------------------------*/

                    const isMultiBookingDetailFlow = isBookingMultiBooking(bookingResponse)
                    if (isMultiBookingDetailFlow) {
                        updateRecoilState('bookingResponseAtom', bookingResponse);
                        prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" });
                        await prepareBookingSummaryAction.execute();
                    } else if (selectedTripsData?.tripIds && selectedTripsData?.tripIds?.length > 0 && selectedTripsData?.passengerData) {
                        // Passenger-scoped filtering path — takes priority over direction-only filtering.
                        // This handles CDT partial-passenger splits, partial cancellations, and other
                        // after-sales scenarios where each card targets specific trips + passengers.
                        const allTrips = bookingResponse.booking?.trips || [];
                        const allBookedOffers = bookingResponse.booking?.bookedOffers || [];

                        const outboundPassengerIds = new Set<string>(selectedTripsData?.passengerData?.outbound || []);
                        const inboundPassengerIds = new Set<string>(selectedTripsData?.passengerData?.inbound || []);
                        const combinedPassengerIds = new Set<string>([
                            ...outboundPassengerIds,
                            ...inboundPassengerIds,
                        ]);

                        const getPassengerIdsForTrip = (trip: any) => {
                            if (trip.direction === TripTypeEnum.OUT_BOUND) {
                                return outboundPassengerIds;
                            }
                            if (trip.direction === TripTypeEnum.IN_BOUND) {
                                return inboundPassengerIds;
                            }
                            return combinedPassengerIds;
                        };

                        const keptTrips = allTrips.filter(
                            trip =>
                                selectedTripsData.tripIds.includes(trip.id) ||
                                (
                                    trip.legs &&
                                    trip.legs.length === 1 &&
                                    trip.legs[0].timedLeg?.service?.mode?.ptMode === "BUS" &&
                                    (() => {
                                        const passengerIdsForTrip = getPassengerIdsForTrip(trip);
                                        if (passengerIdsForTrip.size === 0) {
                                            return false;
                                        }
                                        return allBookedOffers.some((offer: any) =>
                                            offer?.tripCoverage?.coveredTripId === trip.id &&
                                            Array.isArray(offer.admissions) &&
                                            offer.admissions.some(
                                                (a: any) =>
                                                    a?.status === "FULFILLED" &&
                                                    Array.isArray(a.passengerIds) &&
                                                    a.passengerIds.some((pid: string) => passengerIdsForTrip.has(pid))
                                            )
                                        );
                                    })()
                                )
                        );

                        const keptTripIds = keptTrips.map(trip => trip.id);

                        // Detect partial-passenger view: the card covers fewer passengers
                        // than the full booking. For partial views (CDT split, partial cancel),
                        // filter admissions to FULFILLED only. For full booking views, keep all.
                        const totalBookingPassengers = bookingResponse.booking?.passengers?.length || 0;
                        const isPartialPassengerView = combinedPassengerIds.size > 0 && combinedPassengerIds.size < totalBookingPassengers;

                        const filteredOffers = allBookedOffers
                            .filter((offer: any) => {
                                const coveredId = offer.tripCoverage?.coveredTripId;
                                return keptTripIds.includes(coveredId);
                            })
                            .map((offer: any) => {
                                if (combinedPassengerIds.size === 0) return offer;
                                const scopedAdmissions = (offer.admissions || []).filter((admission: any) => {
                                    const passengerMatch = admission.passengerIds?.some((pid: string) => combinedPassengerIds.has(pid));
                                    // Only filter to FULFILLED for partial views (CDT, partial cancel)
                                    const statusMatch = !isPartialPassengerView || admission.status === "FULFILLED";
                                    return passengerMatch && statusMatch;
                                });
                                return { ...offer, admissions: scopedAdmissions };
                            })
                            .filter((offer: any) => (offer.admissions || []).length > 0);

                        // Filter passengers to only those in this card's group
                        const allPassengers = bookingResponse.booking?.passengers || [];
                        const filteredPassengers = combinedPassengerIds.size > 0
                            ? allPassengers.filter((p: any) => combinedPassengerIds.has(p.id))
                            : allPassengers;

                        const updatedBooking = {
                            ...bookingResponse.booking,
                            trips: keptTrips,
                            bookedOffers: filteredOffers,
                            passengers: filteredPassengers
                        };

                        const updatedBookingResponse = {
                            ...bookingResponse,
                            booking: updatedBooking
                        };

                        updateRecoilState('bookingResponseAtom', updatedBookingResponse);

                        prepareBookingSummaryAction.setParams({ ...updatedBooking, isSplit: true, onlyFulfillPax: true, isManageSeating: isManageSeating, isFrom: "booking-details" });
                        await prepareBookingSummaryAction.execute();

                        if (shouldRequestUpgrade(updatedBookingResponse.booking) && !hasOnlyShuttleLegs(updatedBookingResponse.booking) && !areAllPassengersInLatestTripsAlreadyUpgraded(updatedBookingResponse.booking)) {
                            await requestUpgradeOffersForBooking(updatedBookingResponse.booking as any);
                        }
                    } else if (direction) {
                        const allTrips = bookingResponse.booking?.trips || [];
                        const allBookedOffers = bookingResponse.booking?.bookedOffers || [];
                        const allPassengers = bookingResponse.booking?.passengers || [];

                        // Check if this is a round-trip booking (has both outbound and inbound trips).
                        // Prefer trips with a TRAIN leg — a standalone onward-shuttle trip
                        // can share the same direction as the train trip and must not
                        // hijack this lookup (see preferTrainTrips doc).
                        const outboundTrip = preferTrainTrips(allTrips).find((trip: any) => trip.direction === TripTypeEnum.OUT_BOUND);
                        const inboundTrip = preferTrainTrips(allTrips).find((trip: any) => trip.direction === TripTypeEnum.IN_BOUND);
                        const isRoundTrip = outboundTrip && inboundTrip;

                        // Check if this is a split booking (one leg past, one leg future)
                        // For split bookings, we should respect the direction filter
                        let isSplitBooking = false;
                        if (isRoundTrip) {
                            const now = new Date();
                            // Adjust for UAE timezone (UTC+4)
                            now.setUTCHours(now.getUTCHours() + 4);

                            const outboundStartTime = outboundTrip.startTime ? new Date(outboundTrip.startTime) : null;
                            const inboundStartTime = inboundTrip.startTime ? new Date(inboundTrip.startTime) : null;

                            if (outboundStartTime && inboundStartTime) {
                                const isOutboundPast = outboundStartTime < now;
                                const isInboundPast = inboundStartTime < now;
                                // Split booking: one is past and one is future
                                isSplitBooking = isOutboundPast !== isInboundPast;
                            }
                        }

                        // For round-trip bookings that are NOT split, show the full booking without filtering by direction
                        if (isRoundTrip && !isSplitBooking) {
                            updateRecoilState('bookingResponseAtom', bookingResponse);

                            if (shouldRequestUpgrade(bookingResponse.booking) &&
                                !hasOnlyShuttleLegs(bookingResponse.booking) &&
                                !areAllPassengersInLatestTripsAlreadyUpgraded(bookingResponse.booking)) {
                                await requestUpgradeOffersForBooking(bookingResponse.booking as any);
                            }
                            prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" });
                            await prepareBookingSummaryAction.execute();
                        } else {
                            // For one-way bookings, apply direction filtering
                            let directionToKeep = "";
                            let directionToRemove = "";

                            if (direction === TrainTypeEnum.outbound) {
                                directionToKeep = TripTypeEnum.OUT_BOUND;
                                directionToRemove = TripTypeEnum.IN_BOUND;
                            } else if (direction === TrainTypeEnum.inbound) {
                                directionToKeep = TripTypeEnum.IN_BOUND;
                                directionToRemove = TripTypeEnum.OUT_BOUND;
                            }

                            if (directionToKeep && directionToRemove) {
                                // Filter trips to keep only the desired direction
                                let keptTrips = allTrips.filter((trip: any) => trip.direction === directionToKeep);

                                // If multiple keptTrips, check for single-leg trips with inactive legs and remove them
                                if (keptTrips.length > 1) {
                                    keptTrips = keptTrips.filter((trip: any) => {
                                        if (Array.isArray(trip.legs) && trip.legs.length === 1) {
                                            return trip.legs[0]?.isActive === true;
                                        }
                                        return true; // keep trips with multiple legs or no legs
                                    });
                                }

                                // Identify IDs of removed trips (opposite direction)
                                const removedTripIds = allTrips
                                    .filter((trip: any) => trip.direction === directionToRemove)
                                    .map((trip: any) => trip.id);

                                // Filter bookedOffers to exclude those linked to removed trips
                                const filteredOffers = allBookedOffers.filter((offer: any) => {
                                    const coveredId = offer.tripCoverage?.coveredTripId;
                                    return !removedTripIds.includes(coveredId);
                                });

                                // If displayPassengerData is provided, filter passengers by the IDs in displayPassengerData
                                let filteredPassengers = allPassengers;
                                if (displayPassengerData && displayPassengerData.length > 0) {
                                    const displayPassengerIds = displayPassengerData.map((data: any) => data.id);
                                    filteredPassengers = allPassengers.filter((passenger) =>
                                        displayPassengerIds.includes(passenger.id)
                                    );
                                }

                                // Construct updated booking object
                                const updatedBooking = {
                                    ...bookingResponse.booking,
                                    trips: keptTrips,
                                    bookedOffers: filteredOffers,
                                    passengers: filteredPassengers
                                };

                                const updatedBookingResponse = {
                                    ...bookingResponse,
                                    booking: updatedBooking
                                };

                                // Update state and execute summary action
                                updateRecoilState('bookingResponseAtom', updatedBookingResponse);

                                prepareBookingSummaryAction.setParams({ ...updatedBooking, isSplit: true, isManageSeating: isManageSeating, isFrom: "booking-details" });
                                await prepareBookingSummaryAction.execute();

                                if (shouldRequestUpgrade(updatedBookingResponse.booking) && !hasOnlyShuttleLegs(updatedBookingResponse.booking)) {
                                    await requestUpgradeOffersForBooking(updatedBookingResponse.booking as any);
                                }
                            }
                        }
                    }
                    else {
                        updateRecoilState('bookingResponseAtom', bookingResponse);
                        const isChangeDateFlow = localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) || "false";
                        const trips = bookingResponse.booking?.trips || [];
                        const isOneWayTrip = trips.length > 0 && trips.every(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                        
                        if (shouldRequestUpgrade(bookingResponse.booking) && 
                            !hasOnlyShuttleLegs(bookingResponse.booking) &&
                            !(isChangeDateFlow === "true" && isOneWayTrip) && 
                            !areAllPassengersInLatestTripsAlreadyUpgraded(bookingResponse.booking)) {
                            await requestUpgradeOffersForBooking(bookingResponse.booking as any);
                        }
                        prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" })
                        await prepareBookingSummaryAction.execute()

                        
                    }

                    /**
                     * This is only for when user try to do guest booking
                     * It will retrieve and update recoil to show booking on 
                     * Upcoming page and also whenever redirect to upcoming page.
                    */
                    if (isAnonymous) {
                        localStorage.setItem("retrieveBookingCacheData", JSON.stringify({
                            retrieveBooking: {
                                bookingId: bookingId,
                            },
                            reference: [],
                            lastName: []
                        }))
                    } else {
                        localStorage.removeItem("retrieveBookingCacheData")
                    }

                    /**
                     * This is to show newly added addons success message and
                     * show toast for removed addons.
                     * This is only for is @var isManageSeating is true
                    */                    
                    if (isManageSeating) {
                        const needToShowAddedAddons = sessionStorage.getItem("needToShowAddedAddons")
                        const needToShowRemovedAddons = sessionStorage.getItem("needToShowRemovedAddons")
                        if (needToShowAddedAddons === "yes") {
                            showNotification(
                                "success",
                                "Add-ons Added",
                                "New add-ons have been successfully added to your booking.",
                            );
                        }
                        if (needToShowRemovedAddons === "yes") {
                            showNotification(
                                "info",
                                "Add-ons Cancelled",
                                "Add-ons cancellation confirmed. You will not receive a refund for cancellations made less than 48 hours before departure.",
                            );
                        }
                    }
                    sessionStorage.removeItem("needToShowAddedAddons")
                    sessionStorage.removeItem("needToShowRemovedAddons")
                    updateRecoilState("isManageSeatingAtom", isManageSeating);
                } else {
                    console.warn("Failed to fetch Data.");
                }


                /**
                 * Get list of service request for this booking
                */
                await getListOfServiceRequestAction.setParams({ bookingId })
                await getListOfServiceRequestAction.execute()
                if (isActionRunSuccessfully(getListOfServiceRequestAction)) {
                    const result = getListOfServiceRequestAction?.result
                    if (result?.status === "success") {

                    }
                }
            }
        } catch (error: any) {
            console.error("Error fetching Data:", error);
        }
    }
}

export const getBookingDetailDataAction = new GetBookingDetailDataAction()